import { makeObservable, observable, action, runInAction } from 'mobx';
import { isCriticalNotification } from '@featit/shared/notification-severity';
import {
  drawnNoticeKinds,
  NOTICE_PLACES,
  type WaitingNoticeCountsType,
} from '@featit/shared/notification-destination';
import { withheldCount } from '@featit/shared/page-counts';
import { type INotification } from '@featit/shared/types/index';
import { notificationApi } from '@/services/NotificationApi';
import { describeLoadFailure, type ILoadFailure } from '@/services/loadFailure';
import {
  createAttentionPoller,
  PUSH_SETTLE_MS,
  type AttentionPoller,
  type AttentionSubscriber,
} from './attentionPoller';

/** F-0744: how many notices the lists read at a time; the section says when there are more. */
export const NOTICE_PAGE = 100;

/**
 * The reader's notices that Decisions and Tasks for me draw (F-0744), and the unread counts.
 *
 * Since F-0744 there is no bell. The list holds the **unread** notices of the kinds those two lists
 * draw (`drawnNoticeKinds`), never a record kind; both lists and their badges read it. The unread
 * count is the change signal: when it moves, the list is read again.
 *
 * Re-read on the same loop as the decisions count (I-0635, `createAttentionPoller`): every 30 s while
 * the window is focused, every 60 s while the tab is hidden or unfocused, and at once on `focus`,
 * on `visibilitychange` to visible and on a back-forward-cache `pageshow`. Before I-0635 it stopped
 * on `blur` and did not re-read on `focus` at all, so a tab that sat behind another window came back
 * showing the count from when it was left, for up to half a minute more.
 *
 * There is no push for notifications: the server's live streams are per work area and carry item
 * changes, not a reader's inbox. The poll is how a notification arrives.
 *
 * ⚠️ Safari suspends a tab that stays hidden for long; no script runs there, so its count is as old
 * as the suspension until the tab is shown, and then it re-reads at once.
 */
/** A server count: a non-negative whole number. Anything else is not one, whatever its type says. */
function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

/**
 * The server's count of waiting notices, when it is one: every place with its person count and its
 * counts by work area. Anything else (an older server's nothing, a proxy's `{}`) is no count, and
 * the badges count the notices on hand instead (`noticesWaitingInScope`).
 */
function waitingCounts(value: unknown): WaitingNoticeCountsType | null {
  if (!value || typeof value !== 'object') return null;
  const counts = value as Record<string, unknown>;
  const whole = NOTICE_PLACES.every((place) => {
    const count = counts[place] as { person?: unknown; byWorkArea?: unknown } | undefined;
    return (
      isCount(count?.person) &&
      typeof count?.byWorkArea === 'object' &&
      count.byWorkArea !== null &&
      Object.values(count.byWorkArea).every(isCount)
    );
  });
  return whole ? (value as WaitingNoticeCountsType) : null;
}

/** Thrown for a 200 whose body is not a page of notices, so it is told as a failed read. */
class NotAPageError extends Error {
  constructor() {
    super('The notifications answer was not a page of notices.');
  }
}

export class NotificationStore {
  notifications: INotification[] = [];
  /** The list has been read once (F-0744): until then the lists' notice counts are not known. */
  noticesRead = false;
  /**
   * I-2382 (AC-NB17): every waiting notice, counted by the server by place and work area, as the
   * last read answered it; null from a server that predates the count. The badges read this, never
   * the length of the page.
   */
  waiting: WaitingNoticeCountsType | null = null;
  /**
   * How many notifications the server holds for the reader, as the list's own read answered it, or
   * null until one has (I-0552). The list shows a page of them; this is what the page is a part of.
   */
  total: number | null = null;
  /** A further page is being read, so the control that asks for it says so and is pressed once. */
  loadingMore = false;
  /**
   * `null` until a read has answered with a count (I-0661). Starting at `0` let the bell say "no
   * unread" about a number nobody had read, and a response without a count put `undefined` into its
   * accessible name. Unknown is a state of its own, and the bell names it as just "Notifications".
   */
  unreadCount: number | null = null;
  /** Unread notifications the policy calls critical (F-0206) — counted by the server, never guessed. */
  criticalUnreadCount = 0;
  loading = false;
  /**
   * I-2122: why the list's last read did not answer, or null. A read that failed (a 408 at the
   * server's deadline, a 500, no connection) is not an empty inbox, and the dropdown says so with
   * Try again. Only the list read sets it; the unread poll stays quiet, because its failure leaves
   * the badge unknown and the next poll asks again.
   */
  loadFailure: ILoadFailure | null = null;
  /**
   * What went wrong with the last thing the reader asked for, or null.
   *
   * Both mark-as-read calls used to swallow every error in an empty catch, and both returned 500
   * for the whole life of the client (I-0206). The button did nothing, said nothing, and logged nothing —
   * so an inbox that could never be cleared grew to 2,147 unread and read as a volume problem
   * rather than as the broken control it was. A mutation that fails has to say so; that is the
   * difference between a bug somebody reports in a week and one that hides for months.
   *
   * Reads are deliberately not covered by this: a failed poll is transient, recovers by itself, and
   * an error banner appearing every thirty seconds on a flaky connection is its own defect.
   */
  actionError: string | null = null;

  private poller: AttentionPoller | null = null;
  private unreadCountRead: Promise<void> | null = null;
  private unsubscribeAttention: (() => void) | null = null;
  private readonly wakeAttention: (() => void) | null;

  constructor(
    options: { subscribeToAttention?: AttentionSubscriber; wakeAttention?: () => void } = {},
  ) {
    this.wakeAttention = options.wakeAttention ?? null;
    makeObservable(this, {
      notifications: observable,
      noticesRead: observable,
      waiting: observable,
      total: observable,
      loadingMore: observable,
      loadMore: action,
      unreadCount: observable,
      criticalUnreadCount: observable,
      loading: observable,
      loadFailure: observable,
      fetchNotifications: action,
      fetchUnreadCount: action,
      actionError: observable,
      markAsRead: action,
      clearActionError: action,
    });

    if (typeof window !== 'undefined') {
      this.poller = createAttentionPoller({ read: () => this.fetchUnreadCount() });
      // F-0236: the attention stream carries the new unread count, so the bell moves at once; the
      // read that follows brings the critical count, which the signal does not carry.
      this.unsubscribeAttention =
        options.subscribeToAttention?.({
          onChange: (change) => {
            runInAction(() => {
              if (isCount(change.unreadCount)) this.unreadCount = change.unreadCount;
            });
            this.poller?.soon(PUSH_SETTLE_MS);
          },
          onResume: () => this.poller?.soon(0),
        }) ?? null;
    }
  }

  /** The count the list was last read at, so a poll that finds it moved reads the list again. */
  private noticesReadAtCount: number | null = null;

  /**
   * F-0744 (AC-NB11): the unread notices of the kinds the lists draw, newest first. A notice marked
   * read on this visit stays in the list, drawn as read, until this reads again. I-2382 (AC-NB16):
   * only those that still wait on the reader — none about a closed item — with the count of all.
   */
  async fetchNotifications(limit = NOTICE_PAGE) {
    this.loading = true;
    try {
      const page = await notificationApi.findAll(true, limit, 0, drawnNoticeKinds(), true);
      // Not a page (a proxy's or an older server's `{}`): a list that could not be read, never an
      // `undefined` list every badge read in `Layout` would throw on.
      if (!Array.isArray(page?.data)) throw new NotAPageError();
      runInAction(() => {
        this.loadFailure = null;
        this.noticesRead = true;
        this.notifications = page.data;
        this.waiting = waitingCounts(page.waiting);
        this.total = isCount(page.total) ? page.total : null;
        this.loading = false;
      });
    } catch (caught) {
      runInAction(() => {
        this.loadFailure = describeLoadFailure(caught);
        this.loading = false;
      });
    }
  }

  /**
   * How many of the reader's notifications the list does not show: the one figure the dropdown's
   * "Showing the newest 20 of 57" and its Show more are made from. Worked out by `withheldCount` in
   * `@featit/shared`, the arithmetic the CLI's paging uses; null where no total has been read.
   */
  get withheld(): number | null {
    return withheldCount({ shown: this.notifications.length, preceding: 0, total: this.total });
  }

  /** The next page of the list, after the ones already shown; one read at a time (I-0552, AC-2). */
  async loadMore(limit = NOTICE_PAGE): Promise<void> {
    if (this.loadingMore || !this.withheld) return;
    this.loadingMore = true;
    try {
      const page = await notificationApi.findAll(
        true,
        limit,
        this.notifications.filter((n) => !n.isRead).length,
        drawnNoticeKinds(),
        true,
      );
      runInAction(() => {
        const seen = new Set(this.notifications.map((n) => n.id));
        this.notifications = [...this.notifications, ...page.data.filter((n) => !seen.has(n.id))];
        this.total = isCount(page.total) ? page.total : this.total;
      });
    } catch {
      // A failed read leaves the list as it was, and the control to ask again.
    } finally {
      runInAction(() => {
        this.loadingMore = false;
      });
    }
  }

  /**
   * One read at a time, shared by whoever asks while it is in flight (I-0680). The bell is mounted
   * twice — the narrow header's and the sidebar's, one of them hidden by CSS — and each asks on
   * mount, so every page load read the count twice with the same question.
   */
  fetchUnreadCount(): Promise<void> {
    this.unreadCountRead ??= this.readUnreadCount().finally(() => {
      this.unreadCountRead = null;
    });
    return this.unreadCountRead;
  }

  private async readUnreadCount(): Promise<void> {
    try {
      const { count, criticalCount } = await notificationApi.getUnreadCount();
      runInAction(() => {
        // A count that is not a count leaves the last one standing — unknown stays unknown — rather
        // than writing `undefined` or `NaN` into what the bell reads aloud (I-0661).
        if (isCount(count)) this.unreadCount = count;
        this.criticalUnreadCount = isCount(criticalCount) ? criticalCount : 0;
      });
      // A count read means a session: the attention stream may open now (F-0236).
      this.wakeAttention?.();
      // F-0744: the count moved, or the list was never read: the lists' notices are read again.
      if (isCount(count) && count !== this.noticesReadAtCount) {
        this.noticesReadAtCount = count;
        void this.fetchNotifications();
      }
    } catch {
      // silently ignore polling failures
    }
  }

  async markAsRead(notificationId: string) {
    try {
      await notificationApi.markAsRead(notificationId);
      runInAction(() => {
        this.actionError = null;
        const read = this.notifications.find((n) => n.id === notificationId);
        if (read && !read.isRead && isCriticalNotification(read)) {
          this.criticalUnreadCount = Math.max(0, this.criticalUnreadCount - 1);
        }
        this.notifications = this.notifications.map((n) =>
          n.id === notificationId ? { ...n, isRead: true } : n,
        );
        if (this.unreadCount !== null) this.unreadCount = Math.max(0, this.unreadCount - 1);
      });
    } catch {
      runInAction(() => {
        this.actionError = 'That could not be marked as done. Try again.';
      });
    }
  }

  clearActionError() {
    this.actionError = null;
  }

  dispose() {
    this.unsubscribeAttention?.();
    this.unsubscribeAttention = null;
    this.poller?.dispose();
    this.poller = null;
  }
}
